import { Select } from 'antd';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@rockalingua/ui';

import type { CardCredential } from './CardSelection';

interface PaymentMethod {
  id: number | string;
  last4: string;
  data: { default?: boolean };
}

interface SavedCardPickerProps {
  paymentMethods: PaymentMethod[];
  confirmLabel: string;
  onConfirm: (credential: CardCredential) => Promise<void>;
  onCancel: () => void;
  footerSlot: HTMLElement | null;
  disabled?: boolean;
}

export default function SavedCardPicker({
  paymentMethods,
  confirmLabel,
  onConfirm,
  onCancel,
  footerSlot,
  disabled = false,
}: SavedCardPickerProps) {
  const [selectedId, setSelectedId] = useState<number | string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fallback =
      paymentMethods.find((method) => method.data.default) ?? paymentMethods[0];
    setSelectedId(fallback ? fallback.id : null);
  }, [paymentMethods]);

  const confirm = async () => {
    setProcessing(true);
    try {
      await onConfirm({ paymentMethodId: selectedId! });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Select
        size="large"
        value={selectedId}
        onSelect={(value: number | string) => setSelectedId(value)}
        style={{ width: '100%' }}
      >
        {paymentMethods.map((method) => (
          <Select.Option key={method.id} value={method.id}>
            **** **** **** {method.last4}
          </Select.Option>
        ))}
      </Select>

      {footerSlot &&
        createPortal(
          <>
            <Button onClick={onCancel} disabled={processing}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={processing}
              disabled={!selectedId || disabled}
              onClick={confirm}
            >
              {confirmLabel}
            </Button>
          </>,
          footerSlot
        )}
    </>
  );
}
